<app-catalog-page-card title="SWAPS CLONING" [wide]="true">
  <div class="swaps-cloning-header">
    <p class="text-sm text-secondary-foreground">
      Clone swap setup from a source trade into a target trade using the backend cloning rules.
    </p>
  </div>

  <div class="swaps-cloning-stepper" aria-label="Swaps cloning steps">
    <div class="swaps-cloning-step" [class.active]="isCurrentStep(1)">
      <span class="swaps-cloning-step-index">1</span>
      <span>Select source</span>
    </div>
    <div class="swaps-cloning-step" [class.active]="isCurrentStep(2)">
      <span class="swaps-cloning-step-index">2</span>
      <span>Select target</span>
    </div>
    <div class="swaps-cloning-step" [class.active]="isCurrentStep(3)">
      <span class="swaps-cloning-step-index">3</span>
      <span>Confirm and execute</span>
    </div>
  </div>

  @if (isCurrentStep(1)) {
    <section class="grid gap-4">
      <div class="flex items-center justify-between gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Search source swaps..."
          class="kt-input sm:w-72"
          [attr.data-kt-datatable-search]="sourceDatatableSelector"
        />
        <button type="button" class="kt-btn kt-btn-outline kt-btn-icon" aria-label="Refresh source swaps" (click)="loadSources()">
          <i class="ki-filled ki-arrows-circle"></i>
        </button>
      </div>

      @if (isLoadingSources()) {
        <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading source swaps">
          <span class="catalog-loading-spinner" aria-hidden="true"></span>
          <span class="catalog-loading-text">Loading...</span>
        </div>
      } @else if (sourceError()) {
        <div class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
          <span>{{ sourceError() }}</span>
          <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="loadSources()">Retry</button>
        </div>
      } @else {
        <div
          id="kt_swaps_cloning_source_datatable"
          class="kt-card-table swaps-cloning-table-compact"
          data-kt-datatable="true"
          data-kt-datatable-page-size="20"
          data-kt-datatable-state-save="false"
          data-kt-datatable-state-namespace="swaps-cloning-source-datatable"
          (click)="onSourceTableActionClick($event)"
        >
          <div class="kt-table-wrapper kt-scrollable">
            <table class="kt-table" data-kt-datatable-table="true">
              <thead>
                <tr>
                  <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="tradeId"><span class="kt-table-col"><span class="kt-table-col-label">Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="rfrTradeId"><span class="kt-table-col"><span class="kt-table-col-label">RFR Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="structureId"><span class="kt-table-col"><span class="kt-table-col-label">Structure Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="counterparty"><span class="kt-table-col"><span class="kt-table-col-label">Counterparty</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="settlementDate"><span class="kt-table-col"><span class="kt-table-col-label">Settlement Date</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="maturityDate"><span class="kt-table-col"><span class="kt-table-col-label">Maturity Date</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="payLegNotional" class="text-end"><span class="kt-table-col justify-end"><span class="kt-table-col-label">Pay Leg Notional</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="receiveLegNotional" class="text-end"><span class="kt-table-col justify-end"><span class="kt-table-col-label">Receive Leg Notional</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="actions" data-kt-datatable-column-sort="false" class="text-end">Select</th>
                </tr>
              </thead>
              <tbody>
                @if (sources().length === 0) {
                  <tr><td colspan="11" class="text-center text-secondary-foreground py-6">No source swaps available.</td></tr>
                } @else {
                  @for (row of sources(); track row.tradeId) {
                    <tr [class.swaps-cloning-row-selected]="selectedSource()?.tradeId === row.tradeId">
                      <td>{{ row.dealId || '-' }}</td>
                      <td>
                        <a class="text-primary hover:underline" [routerLink]="['/swap-detail', row.tradeId]" target="_blank" rel="noopener">
                          {{ row.tradeId }}
                        </a>
                      </td>
                      <td>{{ row.rfrTradeId || '-' }}</td>
                      <td>{{ row.structureId || '-' }}</td>
                      <td>{{ row.counterparty || '-' }}</td>
                      <td>{{ formatDate(row.tradeDate) }}</td>
                      <td>{{ formatDate(row.settlementDate) }}</td>
                      <td>{{ formatDate(row.maturityDate) }}</td>
                      <td class="text-end">{{ formatNotional(row.payLegCurrency, row.payLegNotional) }}</td>
                      <td class="text-end">{{ formatNotional(row.receiveLegCurrency, row.receiveLegNotional) }}</td>
                      <td class="text-end">
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-primary swaps-cloning-select-icon"
                          data-action="select-source-swap"
                          [attr.data-source-trade-id]="row.tradeId"
                          aria-label="Select source swap"
                          title="Select source swap"
                        >
                          <i class="ki-filled ki-check-circle" aria-hidden="true"></i>
                        </button>
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
          <div class="flex items-center justify-between gap-4 flex-wrap px-5 py-4">
            <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
            <div class="kt-datatable-length flex items-center gap-3">
              <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
              <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
            </div>
          </div>
        </div>
      }
    </section>
  }

  @if (isCurrentStep(2)) {
    <section class="grid gap-4">
      <div class="swaps-cloning-summary-grid">
        <div class="kt-card p-4">
          <h3 class="text-sm font-semibold text-mono mb-1">Selected source</h3>
          <p class="text-sm">{{ selectedSource()?.tradeId || '-' }} · {{ selectedSource()?.dealId || '-' }}</p>
        </div>
        <div class="kt-card p-4">
          <h3 class="text-sm font-semibold text-mono mb-1">Selected target</h3>
          <p class="text-sm">{{ selectedTarget()?.tradeId || '-' }} · {{ selectedTarget()?.dealId || '-' }}</p>
        </div>
      </div>

      <div class="flex items-center justify-between gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Search target swaps..."
          class="kt-input sm:w-72"
          [attr.data-kt-datatable-search]="targetDatatableSelector"
        />
        <div class="inline-flex items-center gap-2">
          <button type="button" class="kt-btn kt-btn-outline" (click)="onBackToSourceStep()">Back</button>
          <button type="button" class="kt-btn kt-btn-outline kt-btn-icon" aria-label="Refresh target swaps" (click)="loadTargets()">
            <i class="ki-filled ki-arrows-circle"></i>
          </button>
        </div>
      </div>

      @if (isLoadingTargets()) {
        <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading target swaps">
          <span class="catalog-loading-spinner" aria-hidden="true"></span>
          <span class="catalog-loading-text">Loading...</span>
        </div>
      } @else if (targetError()) {
        <div class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
          <span>{{ targetError() }}</span>
          <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="loadTargets()">Retry</button>
        </div>
      } @else {
        <div
          id="kt_swaps_cloning_target_datatable"
          class="kt-card-table swaps-cloning-table-compact"
          data-kt-datatable="true"
          data-kt-datatable-page-size="20"
          data-kt-datatable-state-save="false"
          data-kt-datatable-state-namespace="swaps-cloning-target-datatable"
          (click)="onTargetTableActionClick($event)"
        >
          <div class="kt-table-wrapper kt-scrollable">
            <table class="kt-table" data-kt-datatable-table="true">
              <thead>
                <tr>
                  <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="tradeId"><span class="kt-table-col"><span class="kt-table-col-label">Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="rfrTradeId"><span class="kt-table-col"><span class="kt-table-col-label">RFR Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="structureId"><span class="kt-table-col"><span class="kt-table-col-label">Structure Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="counterparty"><span class="kt-table-col"><span class="kt-table-col-label">Counterparty</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="settlementDate"><span class="kt-table-col"><span class="kt-table-col-label">Settlement Date</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="maturityDate"><span class="kt-table-col"><span class="kt-table-col-label">Maturity Date</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="payLegNotional" class="text-end"><span class="kt-table-col justify-end"><span class="kt-table-col-label">Pay Leg Notional</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="receiveLegNotional" class="text-end"><span class="kt-table-col justify-end"><span class="kt-table-col-label">Receive Leg Notional</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="actions" data-kt-datatable-column-sort="false" class="text-end">Select</th>
                </tr>
              </thead>
              <tbody>
                @if (targets().length === 0) {
                  <tr><td colspan="11" class="text-center text-secondary-foreground py-6">No target swaps available for this source.</td></tr>
                } @else {
                  @for (row of targets(); track row.tradeId) {
                    <tr [class.swaps-cloning-row-selected]="selectedTarget()?.tradeId === row.tradeId">
                      <td>{{ row.dealId || '-' }}</td>
                      <td>
                        <a class="text-primary hover:underline" [routerLink]="['/swap-detail', row.tradeId]" target="_blank" rel="noopener">
                          {{ row.tradeId }}
                        </a>
                      </td>
                      <td>{{ row.rfrTradeId || '-' }}</td>
                      <td>{{ row.structureId || '-' }}</td>
                      <td>{{ row.counterparty || '-' }}</td>
                      <td>{{ formatDate(row.tradeDate) }}</td>
                      <td>{{ formatDate(row.settlementDate) }}</td>
                      <td>{{ formatDate(row.maturityDate) }}</td>
                      <td class="text-end">{{ formatNotional(row.payLegCurrency, row.payLegNotional) }}</td>
                      <td class="text-end">{{ formatNotional(row.receiveLegCurrency, row.receiveLegNotional) }}</td>
                      <td class="text-end">
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-primary swaps-cloning-select-icon"
                          data-action="select-target-swap"
                          [attr.data-target-trade-id]="row.tradeId"
                          aria-label="Select target swap"
                          title="Select target swap"
                        >
                          <i class="ki-filled ki-check-circle" aria-hidden="true"></i>
                        </button>
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
          <div class="flex items-center justify-between gap-4 flex-wrap px-5 py-4">
            <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
            <div class="kt-datatable-length flex items-center gap-3">
              <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
              <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
            </div>
          </div>
        </div>
      }
    </section>
  }

  @if (isCurrentStep(3)) {
    <section class="grid gap-4">
      <div class="kt-card p-6 lg:p-8 swaps-cloning-confirmation-card">
        <h2 class="text-xl lg:text-2xl font-semibold text-mono">
          You are about to clone information between these swaps. Do you want to proceed?
        </h2>
        <div class="mt-4 swaps-cloning-confirmation-grid">
          <div class="kt-card p-4">
            <h3 class="text-sm font-semibold text-mono mb-2">Source swap</h3>
            <p class="text-sm text-secondary-foreground">Deal ID: <strong>{{ selectedSource()?.dealId || '-' }}</strong></p>
            <p class="text-sm text-secondary-foreground mt-1">Trade ID: <strong>{{ selectedSource()?.tradeId || '-' }}</strong></p>
          </div>
          <div class="kt-card p-4">
            <h3 class="text-sm font-semibold text-mono mb-2">Target swap</h3>
            <p class="text-sm text-secondary-foreground">Deal ID: <strong>{{ selectedTarget()?.dealId || '-' }}</strong></p>
            <p class="text-sm text-secondary-foreground mt-1">Trade ID: <strong>{{ selectedTarget()?.tradeId || '-' }}</strong></p>
          </div>
        </div>
        <p class="mt-1 text-base text-secondary-foreground">
          Resolved mode: <strong>{{ resolvedCloneType() }}</strong>. All FRIDA information currently stored on the target swap will be overwritten.
        </p>
      </div>

      @if (executeError()) {
        <div class="kt-alert kt-alert-destructive">{{ executeError() }}</div>
      }

      @if (executeResult(); as execution) {
        <div class="kt-alert kt-alert-success">
          <div class="kt-alert-title"><strong>Swap cloning completed.</strong></div>
          <div class="kt-alert-text">Target: {{ execution.targetTradeId }} · Executed at: {{ formatDateTime(execution.executedAtUtc) }}</div>
          <div class="mt-2">
            <a class="text-primary hover:underline" [routerLink]="['/swap-detail', execution.targetTradeId]" target="_blank" rel="noopener">
              Open target swap details
            </a>
          </div>
        </div>
      }

      <div class="flex items-center justify-center gap-2">
        <button type="button" class="kt-btn kt-btn-outline" (click)="onBackToTargetStep()">Back</button>
        <button type="button" class="kt-btn kt-btn-primary" (click)="onExecute()" [disabled]="isExecuting()">
          @if (!isExecuting()) { Proceed } @else { Processing... }
        </button>
      </div>
    </section>
  }
</app-catalog-page-card>
